import type { ReactNode, Ref } from "react";
import { cn } from "@/shared/cn";

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = "md",
  wrap = true,
  trailing,
  ref,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  size?: "md" | "sm";
  wrap?: boolean;
  trailing?: ReactNode;
  ref?: Ref<HTMLFieldSetElement>;
}) {
  return (
    <fieldset
      ref={ref}
      aria-label={label}
      className={cn(
        "m-0 inline-flex min-w-0 border-0 max-w-full gap-[2px] rounded-[12px] bg-(--fill) p-[4px]",
        wrap ? "flex-wrap" : "shrink-0 flex-nowrap",
      )}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(option.value)}
            className={segmentClass(pressed, size)}
          >
            {option.label}
          </button>
        );
      })}
      {trailing}
    </fieldset>
  );
}

export function segmentClass(pressed: boolean, size: "md" | "sm" = "md"): string {
  return cn(
    "cursor-pointer rounded-[9px] font-medium uppercase leading-[1.2] tracking-[0.06em]",
    size === "sm" ? "px-[10px] py-[5px] text-xs" : "px-[13px] py-[8px] text-xs",
    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)",
    pressed
      ? "bg-(--paper) text-(--ink) shadow-[0_1px_2px_rgba(26,26,26,0.08)]"
      : "text-(--dim) hover:text-(--ink)",
  );
}
